import { AdminHeader } from "@/components/AdminHeader";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { UserPlus, Trash2, Mail, LayoutGrid } from "lucide-react";

const ROW = "border-[#E5DED5] hover:bg-[#F5EFE6]/50 transition-colors";

const TIERS = [
  { key: "manager", label: "Manager", desc: "Full access — add, edit and delete across the panel" },
  { key: "supervisor", label: "Supervisor", desc: "Add & edit — manage bookings and vehicles, no deletions" },
  { key: "employee", label: "Employee", desc: "View only — see bookings, documents and reports" },
];

const TIER_STYLE = {
  manager: "text-[#C71E2E] border-[#C71E2E]/30 bg-[#C71E2E]/10",
  supervisor: "text-sky-700 border-sky-200 bg-sky-50",
  employee: "text-muted-foreground border-[#E5DED5] bg-[#FAF6F0]",
};

const TIER_SECTIONS = {
  manager: ["All 20 sections — full control"],
  supervisor: ["Overview", "Bookings", "Vehicles", "Blackout Dates", "Licenses", "Contracts", "GPS / Locations", "Maintenance", "Discount Codes", "Leads", "Loyalty", "Gallery", "Reviews", "Emails", "Visitors"],
  employee: ["Overview", "Bookings", "Vehicles", "Blackout Dates", "Licenses", "Contracts", "GPS / Locations", "Maintenance"],
};

const ALL_SECTIONS = [
  ["overview", "Overview"], ["bookings", "Bookings"], ["vehicles", "Vehicles"], ["blackouts", "Blackout Dates"],
  ["licenses", "Licenses & Returns"], ["contracts", "Contracts"], ["locations", "GPS / Locations"], ["maintenance", "Maintenance"],
  ["payments", "Payments"], ["pricing", "Smart Pricing"], ["coupons", "Discount Codes"],
  ["loyalty", "Loyalty"], ["reviews", "Reviews"], ["leads", "Leads"], ["gallery", "Gallery"],
  ["users", "Users"], ["tokens", "API Tokens"], ["emails", "Emails"], ["visitors", "Visitors"], ["seo", "SEO & Verification"],
];

const TIER_KEYS = {
  supervisor: ["overview", "bookings", "vehicles", "blackouts", "licenses", "contracts", "locations", "maintenance", "coupons", "leads", "loyalty", "gallery", "reviews", "emails", "visitors"],
  employee: ["overview", "bookings", "vehicles", "blackouts", "licenses", "contracts", "locations", "maintenance"],
};

export default function UsersTab() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ name: "", email: "", staff_role: "employee" });
  const [saving, setSaving] = useState(false);
  const [tabsFor, setTabsFor] = useState(null);
  const [tabSel, setTabSel] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const loadLog = () => api.get("/admin/access-log").then((r) => setAccessLog(r.data)).catch(() => {});
  useEffect(() => { loadLog(); }, []);

  const openTabs = (u) => {
    setTabsFor(u);
    const def = u.staff_role === "manager" ? ALL_SECTIONS.map(([k]) => k) : (TIER_KEYS[u.staff_role] || TIER_KEYS.employee);
    setTabSel(Array.isArray(u.tab_overrides) ? u.tab_overrides : def);
  };

  const saveTabs = async (tabs) => {
    try {
      await api.patch(`/admin/users/${tabsFor.id}/tabs`, { tabs });
      toast.success(tabs === null ? "Back to tier default access" : "Custom section access saved");
      setTabsFor(null);
      load();
      loadLog();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const load = () => api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addUser = async () => {
    if (!form.name.trim() || !/\S+@\S+\.\S+/.test(form.email))
      return toast.error("Name and a valid email are required");
    setSaving(true);
    try {
      const { data } = await api.post("/admin/users", {
        name: form.name.trim(),
        email: form.email.trim(),
        staff_role: form.staff_role,
        origin_url: window.location.origin,
      });
      toast.success(data.email_sent
        ? `${data.staff_role ? data.staff_role[0].toUpperCase() + data.staff_role.slice(1) : "Team member"} invited — welcome email with a temporary password was sent to ${data.email}`
        : "Team member added, but the invite email failed to send — use 'Forgot password' on the admin sign-in page to set their password");
      setForm({ name: "", email: "", staff_role: "employee" });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, staff_role) => {
    try {
      await api.patch(`/admin/users/${u.id}/role`, { staff_role });
      toast.success(`${u.email} is now ${staff_role}`);
      load();
      loadLog();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Remove ${u.email} from the admin panel?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Team member removed");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const filtered = users.filter((u) => {
    const q = search.trim().toLowerCase();
    return !q || [u.name, u.email, u.phone].some((v) => (v || "").toLowerCase().includes(q));
  });

  const isOwner = (u) => u.role === "admin" && !u.permissions;

  return (
    <div data-testid="admin-users-tab">
      <AdminHeader testid="admin-header-users" eyebrow="Access" title="Users" description="Customer accounts and admin team members." actions={<div className="w-full sm:w-72"><Input data-testid="users-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, email or phone…" className="input-dark" /></div>} />

      <div data-testid="add-staff-card" className="mb-8 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A] flex items-center gap-2">
          <UserPlus size={14} className="text-[#C71E2E]" /> Add team member to admin panel
        </p>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Full name</Label>
            <Input data-testid="staff-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Smith" className="input-dark mt-1.5" />
          </div>
          <div>
            <Label className="font-ui text-xs text-muted-foreground">Email</Label>
            <Input data-testid="staff-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="staff@easydrivecarrental.com" className="input-dark mt-1.5" />
          </div>
        </div>
        <p className="mt-3 text-xs font-ui text-muted-foreground flex items-center gap-1.5"><Mail size={12} className="text-[#C71E2E]" /> They'll receive a welcome email with a temporary password and a link to set their own password.</p>
        <p className="mt-5 font-ui text-xs tracking-wide text-muted-foreground">Access level — choose what they can do</p>
        <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {TIERS.map((t) => (
            <button key={t.key} type="button" data-testid={`staff-role-${t.key}`} onClick={() => setForm({ ...form, staff_role: t.key })}
              className={`rounded-xl border p-3.5 text-left transition-all duration-200 ${form.staff_role === t.key ? "border-[#C71E2E] bg-[#C71E2E]/5 ring-1 ring-[#C71E2E]/30" : "border-[#E5DED5] bg-white hover:border-[#C71E2E]/40"}`}>
              <p className="font-ui text-sm font-semibold text-[#1A1A1A]">{t.label}</p>
              <p className="mt-0.5 text-[11px] font-ui text-muted-foreground leading-relaxed">{t.desc}</p>
            </button>
          ))}
        </div>
        <Button data-testid="staff-add-button" onClick={addUser} disabled={saving} className="mt-5 rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui hover:scale-95 transition-transform">
          {saving ? "Sending invite…" : "Send Invite"}
        </Button>
      </div>

      <div className="bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-[#E5DED5] hover:bg-transparent">
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Role</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow className={ROW}>
                <TableCell colSpan={6} className="text-muted-foreground font-ui py-10 text-center">No users found.</TableCell>
              </TableRow>
            )}
            {filtered.map((u) => (
              <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`} className={ROW}>
                <TableCell className="font-ui font-medium">{u.name || "—"}</TableCell>
                <TableCell className="text-muted-foreground">{u.email}</TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">{u.phone || "—"}</TableCell>
                <TableCell>
                  {isOwner(u) ? (
                    <span data-testid={`user-role-${u.id}`} className="text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 text-[#C71E2E] border-[#C71E2E]/30 bg-[#C71E2E]/10">Owner</span>
                  ) : u.role === "admin" ? (
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                      <span data-testid={`user-role-${u.id}`} className="text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 text-[#C71E2E] border-[#C71E2E]/30 bg-[#C71E2E]/10">Team</span>
                      <span data-testid={`user-tier-${u.id}`} className={`text-[10px] font-ui uppercase tracking-wide border rounded-full px-2.5 py-0.5 ${TIER_STYLE[u.staff_role] || TIER_STYLE.employee}`}>{u.staff_role || "employee"}</span>
                      {Array.isArray(u.tab_overrides) && (
                        <span data-testid={`user-custom-${u.id}`} className="text-[9px] font-ui uppercase tracking-wide border rounded-full px-2 py-0.5 text-violet-700 border-violet-300 bg-violet-50">custom sections</span>
                      )}
                    </span>
                  ) : (
                    <span data-testid={`user-role-${u.id}`} className="text-[10px] font-ui tracking-[0.15em] uppercase border rounded-full px-3 py-1 text-muted-foreground border-[#E5DED5] bg-[#FAF6F0]">Customer</span>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground whitespace-nowrap">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"}
                </TableCell>
                <TableCell className="text-right">
                  {u.role === "admin" && !isOwner(u) && (
                    <span className="inline-flex items-center gap-2.5">
                      <select data-testid={`user-role-select-${u.id}`} value={u.staff_role || "employee"} onChange={(e) => changeRole(u, e.target.value)} title="Change access level (owner only)" className="text-xs font-ui border border-[#E5DED5] rounded-full px-2.5 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-[#C71E2E]/30 cursor-pointer">
                        {TIERS.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                      </select>
                      <button data-testid={`user-tabs-${u.id}`} onClick={() => openTabs(u)} title="Customize which sections this person can open" className="text-muted-foreground hover:text-[#C71E2E] transition-colors duration-200" aria-label="Customize section access">
                        <LayoutGrid size={15} />
                      </button>
                      <button data-testid={`user-delete-${u.id}`} onClick={() => removeUser(u)} className="text-muted-foreground hover:text-red-700 transition-colors duration-200" aria-label="Remove team member">
                        <Trash2 size={15} />
                      </button>
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <p data-testid="users-count" className="mt-3 text-xs text-muted-foreground font-ui">{users.length} registered {users.length === 1 ? "user" : "users"}</p>

      <Dialog open={!!tabsFor} onOpenChange={(o) => !o && setTabsFor(null)}>
        <DialogContent data-testid="tabs-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display tracking-tight">Section access — {tabsFor?.name || tabsFor?.email}</DialogTitle>
          </DialogHeader>
          <p className="text-xs font-ui text-muted-foreground leading-relaxed">
            Tier: <b className="capitalize">{tabsFor?.staff_role || "employee"}</b>. Tick exactly which admin sections this person can open — a custom selection overrides the tier default. Write permissions still follow their tier.
          </p>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2.5 max-h-72 overflow-y-auto pr-1">
            {ALL_SECTIONS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2.5 text-sm font-ui cursor-pointer text-[#1A1A1A]">
                <input type="checkbox" data-testid={`tab-access-${k}`} checked={tabSel.includes(k)} onChange={(e) => setTabSel(e.target.checked ? [...tabSel, k] : tabSel.filter((x) => x !== k))} className="accent-[#C71E2E] w-4 h-4" />
                {label}
              </label>
            ))}
          </div>
          <div className="mt-5 flex flex-wrap items-center justify-between gap-2">
            <Button data-testid="tabs-reset-button" variant="outline" onClick={() => saveTabs(null)} disabled={!tabsFor?.tab_overrides} className="rounded-full border-[#E5DED5] bg-transparent hover:bg-[#F5EFE6] font-ui text-xs">
              Reset to tier default
            </Button>
            <Button data-testid="tabs-save-button" onClick={() => saveTabs(tabSel)} className="rounded-full bg-[#C71E2E] text-white hover:bg-[#A01825] font-ui">
              Save custom access ({tabSel.length} sections)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {accessLog.length > 0 && (
        <div data-testid="access-log-card" className="mt-8 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
          <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Access changes — audit trail</p>
          <ul className="mt-3 space-y-2 max-h-48 overflow-y-auto pr-1">
            {accessLog.map((a) => (
              <li key={a.id} data-testid={`access-log-${a.id}`} className="text-xs font-ui text-[#1A1A1A] flex flex-wrap gap-x-2">
                <span className="text-muted-foreground shrink-0">{new Date(a.created_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
                <span><b>{a.actor}</b> {a.action === "invite" ? "invited" : a.action === "removed" ? "removed" : a.action === "role" ? "changed role of" : "changed sections of"} <b>{a.target}</b>{a.detail ? <span className="text-muted-foreground"> — {a.detail}</span> : null}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div data-testid="tier-matrix-card" className="mt-8 bg-white shadow-[0_8px_30px_rgba(0,0,0,0.04)] rounded-2xl p-6">
        <p className="font-ui text-xs tracking-[0.2em] uppercase text-[#4A4A4A]">Access matrix — what each level can see</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {TIERS.map((t) => (
            <div key={t.key} data-testid={`tier-matrix-${t.key}`} className="rounded-xl border border-[#E5DED5] p-4">
              <p className="font-ui text-sm font-semibold text-[#1A1A1A]">{t.label}</p>
              <p className="mt-0.5 text-[11px] font-ui text-muted-foreground">{t.desc}</p>
              <div className="mt-2.5 flex flex-wrap gap-1.5">
                {TIER_SECTIONS[t.key].map((s) => (
                  <span key={s} className="text-[9px] font-ui uppercase tracking-wide border border-[#E5DED5] bg-[#FAF6F0] text-muted-foreground rounded-full px-2 py-0.5">{s}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
